import { Text, Stack, Tooltip, Flex, Box, Tag } from '@chakra-ui/react';
import { CellValue } from 'react-table';

import { IconBreakTime, IconComment, IconFlag, IconImage } from 'assets/icons';
import { IndeterminateCheckbox } from 'components/Table';
import { formatDistance, formatToLongLocalizedDate, formatTwelveHourMinuteTime } from 'utils/format';
import { secondsToMS } from 'utils/helpers';

export const getVisitsCommonColumn = (t: any, showSelection = true) => {
  return [
    ...(showSelection
      ? [
          {
            id: 'selection',
            width: 30,
            colStyles: (data: any) => ({
              background: data?.flagged ? 'var(--timeero-colors-secondary-50)' : 'initial',
            }),
            Header: ({ getToggleAllRowsSelectedProps }: any) => {
              return (
                <IndeterminateCheckbox
                  checkboxStyles={{ colorScheme: 'secondary' }}
                  {...getToggleAllRowsSelectedProps()}
                />
              );
            },
            Cell: ({ row }: any) => {
              return (
                <IndeterminateCheckbox
                  checkboxStyles={{ colorScheme: 'secondary' }}
                  {...row.getToggleRowSelectedProps()}
                />
              );
            },
          },
        ]
      : []),

    {
      Header: t('common:name'),
      width: 150,
      accessor: 'first_name',
      colStyles: (data: any) => ({
        background: data?.flagged ? 'var(--timeero-colors-secondary-50)' : 'initial',
      }),
      Cell: (data: CellValue) => {
        const { minutes, seconds } = secondsToMS(data.row.original?.break_in_seconds ?? 0);

        const breakTooltipLabel = minutes === '00' && seconds === '00' ? '00m 00s' : `${minutes}m ${seconds}s`;

        return (
          <Stack direction="column">
            <Text fontWeight="semibold" fontSize="sm">
              {data.value} {data.row.original?.last_name}
            </Text>
            <Stack direction="row">
              {data.row.original?.notes ? (
                <Tooltip label={data.row.original?.notes} hasArrow bg="black">
                  <Box boxSize="16px">
                    <IconComment />
                  </Box>
                </Tooltip>
              ) : null}
              {data.row.original?.flagged ? (
                <Tooltip label={data.row.original?.flagged_reason} hasArrow bg="black">
                  <Box boxSize="16px" sx={{ '& path': { fill: 'gray.400' } }}>
                    <IconFlag />
                  </Box>
                </Tooltip>
              ) : null}
              {data.row.original?.breaks_available ? (
                <Tooltip label={breakTooltipLabel} hasArrow bg="black">
                  <Box boxSize="16px" sx={{ '& path': { fill: 'gray.400' } }}>
                    <IconBreakTime />
                  </Box>
                </Tooltip>
              ) : null}
              {data.row.original?.attachments ? (
                <Tooltip label="attachments" hasArrow bg="black">
                  <Box boxSize="16px" sx={{ '& path': { fill: 'gray.400' } }}>
                    <IconImage />
                  </Box>
                </Tooltip>
              ) : null}
            </Stack>
          </Stack>
        );
      },
    },
    {
      Header: t('evv:patientOrService'),
      accessor: 'job_name',
      width: 150,
      colStyles: (data: any) => ({
        background: data?.flagged ? 'var(--timeero-colors-secondary-50)' : 'initial',
      }),
      Cell: (data: CellValue) => (
        <Flex flexDirection="column">
          <Text fontWeight="semibold" fontSize="sm">
            {data.value || '- '}
          </Text>
          <Text mt="1" color="#656886" fontSize="sm">
            {data.row.original?.task_name || '-'}
          </Text>
        </Flex>
      ),
    },
    {
      Header: 'Clock In',
      accessor: 'clock_in',
      colStyles: (data: any) => ({
        background: data?.flagged ? 'var(--timeero-colors-secondary-50)' : 'initial',
      }),
      Cell: (data: CellValue) => {
        return (
          <Box>
            <Text>
              {data.value?.local_time ? (
                <>
                  <Text fontWeight="semibold" fontSize="sm" color="#1C2045" letterSpacing="0.01em">
                    {formatTwelveHourMinuteTime(data.value?.local_time)}
                  </Text>
                  <Text color="#656886" fontSize="xs">
                    {formatToLongLocalizedDate(data.value?.local_time)}
                  </Text>
                </>
              ) : (
                'Not clocked in'
              )}
            </Text>
            {data.row?.original?.timezone?.clock_in &&
            data.row?.original?.timezone?.company_tz !== data.row?.original?.timezone?.clock_in ? (
              <Tag bg="secondary.100" size="sm" color="secondary.600" fontSize="xs" fontWeight="semibold">
                {data.row?.original?.timezone?.clock_in}
              </Tag>
            ) : (
              ''
            )}
          </Box>
        );
      },
    },
    {
      Header: 'Clock Out',
      accessor: 'clock_out',
      colStyles: (data: any) => ({
        background: data?.flagged ? 'var(--timeero-colors-secondary-50)' : 'initial',
      }),
      Cell: (data: CellValue) => (
        <Box>
          <Text>
            {data.value?.local_time ? (
              <>
                <Text fontWeight="semibold" fontSize="sm" color="#1C2045" letterSpacing="0.01em">
                  {formatTwelveHourMinuteTime(data.value?.local_time)}
                </Text>
                <Text color="#656886" fontSize="xs">
                  {formatToLongLocalizedDate(data.value?.local_time)}
                </Text>
              </>
            ) : (
              'Not clocked out'
            )}
          </Text>
          {data.row?.original?.timezone?.clock_out &&
          data.row?.original?.timezone?.company_tz !== data.row?.original?.timezone?.clock_out ? (
            <Tag bg="secondary.100" size="sm" color="secondary.600" fontSize="xs" fontWeight="semibold">
              {data.row?.original?.timezone?.clock_out}
            </Tag>
          ) : (
            ''
          )}
        </Box>
      ),
    },
    {
      Header: 'Duration',
      accessor: 'duration',
      colStyles: (data: any) => ({
        background: data?.flagged ? 'var(--timeero-colors-secondary-50)' : 'initial',
      }),
      Cell: (data: CellValue) => (
        <Tag bg="#F1F2F9" color="#1C2045" fontSize="sm" fontWeight="semibold">
          {data.value || '- '}
        </Tag>
      ),
    },
    {
      Header: 'Mileage',
      accessor: 'mileage',
      colStyles: (data: any) => ({
        background: data?.flagged ? 'var(--timeero-colors-secondary-50)' : 'initial',
      }),
      Cell: (data: CellValue) => (
        <Tag bg="#4852AF" color="white" fontSize="sm" fontWeight={300}>
          {!isNaN(data.value) ? formatDistance(data.value) : '-'}
        </Tag>
      ),
    },
  ];
};
